import React from "react";
import { motion } from "framer-motion";
import { MapPin, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { IMAGES } from "../config";
import SEO from "../components/SEO";
import { useBookingModal } from "../context/BookingModalContext";

const aboutSchema = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  "mainEntity": { "@id": "https://www.barspotltd.co.uk/#business" }
};

const serviceAreas = [
  {
    region: "Birmingham & West Midlands",
    description: "Our primary base. Covering Birmingham, Solihull, Wolverhampton, Walsall, Dudley, Coventry, Warwick, Worcester and surrounding areas within approximately 80 miles."
  },
  {
    region: "Cambridge & East England",
    description: "Our second location. Serving Cambridge, Bury St Edmunds, Ipswich, Norwich, Peterborough, Hertfordshire, Essex and surrounding areas within approximately 80 miles."
  },
  {
    region: "Worcestershire",
    description: "Worcester, Malvern, Kidderminster, Redditch, Bromsgrove, Droitwich and the wider Worcestershire area."
  },
  {
    region: "East & West Midlands",
    description: "Nottingham, Leicester, Derby, Northampton, Warwickshire, Staffordshire and the broader Midlands region."
  },
  {
    region: "London & South East",
    description: "Available for bookings in London and the Home Counties. Travel fees may apply."
  },
  {
    region: "UK-Wide",
    description: "We cover the majority of the UK for weddings, corporate events, and festivals. Contact us for availability."
  }
];

const AboutPage = () => {
  const { openBookingModal } = useBookingModal();
  return (
    <div className="pt-20">
      <SEO
        title="About Bar Spot | Mobile Bar Hire Birmingham & Cambridge"
        description="Established 2020. 300+ events, 100,000+ drinks served. Award-winning mobile bar hire based in Birmingham & Cambridge, covering the UK."
        canonical="/about"
        schema={aboutSchema}
      />
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <p className="text-[#a5dae0] font-script text-2xl mb-4">Our Story</p>
              <h1 className="text-5xl mb-6">About Bar Spot</h1>
              <div className="space-y-4 text-[#666666] leading-relaxed">
                <p>
                  Established in 2020, Bar Spot was created with a vision to provide an unrivaled
                  mobile bar experience across the UK. Based in Birmingham and Cambridge, our mission is to build a well-known brand
                  synonymous with quality service, stylish setups, and affordable mobile bar hire.
                </p>
                <p>
                  We're passionate about supporting local and independent businesses, collaborating
                  with like-minded brands to help them thrive and grow. No matter where we set up,
                  Bar Spot is committed to creating a fantastic and memorable bar experience.
                </p>
                <p>
                  With experience in over 300 events and more than 100,000 drinks served, we know
                  that no two events are the same. That's why every booking is approached with a
                  personalized touch — from wedding bar hire in Worcestershire to corporate event bars in Birmingham.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="relative"
            >
              <div className="grid grid-cols-2 gap-4">
                <img src={IMAGES.cocktail} alt="Bartender preparing cocktails at a mobile bar event" className="rounded-2xl h-64 object-cover" loading="lazy" />
                <img src={IMAGES.beerPour} alt="Draught beer being poured at Bar Spot mobile bar" className="rounded-2xl h-64 object-cover mt-8" loading="lazy" />
                <img src={IMAGES.celebration} alt="Guests celebrating at a private party with mobile bar" className="rounded-2xl h-64 object-cover" loading="lazy" />
                <img src={IMAGES.weddingReception} alt="Wedding reception bar setup by Bar Spot" className="rounded-2xl h-64 object-cover mt-8" loading="lazy" />
              </div>
              {/* Floating Logo Badge */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.5, type: "spring" }}
                className="absolute -bottom-8 left-1/2 -translate-x-1/2"
              >
                <img 
                  src={IMAGES.barspotLogoRound} 
                  alt="Bar Spot Ltd logo" 
                  className="w-24 h-24 rounded-full shadow-xl border-4 border-white"
                  loading="lazy"
                />
              </motion.div>
            </motion.div>
          </div>

          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <img src="https://www.addtoevent.co.uk/sites/default/files/badges/featured_landscape_purple_medium.png" alt="Featured on Add to Event — mobile bar hire directory" className="h-16 mx-auto object-contain" loading="lazy" />
            <img src="https://lirp.cdn-website.com/21979d5c/dms3rep/multi/opt/Im-a-member-email-signature-300-1920w.png" alt="NCASS Member — food and drink safety certified" className="h-16 mx-auto object-contain" loading="lazy" />
            <img src="https://www.hitched.co.uk/images/sellos/partner--gg280540.png" alt="Hitched recommended wedding bar hire supplier" className="h-16 mx-auto object-contain" loading="lazy" />
            <img src="https://cdn1.hitched.co.uk/img/badges/2026/badge-weddingawards_en_GB.jpg" alt="Hitched 2026 Wedding Awards winner" className="h-16 mx-auto object-contain" loading="lazy" />
          </div>

          {/* Expanded Service Areas Section */}
          <div className="mt-20">
            <h2 className="text-3xl text-center mb-4">Mobile Bar Hire Service Areas</h2>
            <p className="text-center text-[#555555] max-w-2xl mx-auto mb-10">
              Based in Birmingham and Cambridge, we provide mobile bar hire across the majority of the UK.
              Our main service areas cover approximately 80 miles from each base.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {serviceAreas.map(area => (
                <div key={area.region} className="bg-white rounded-xl p-6 shadow-sm">
                  <h3 className="flex items-center gap-2 font-semibold text-[#1a1a1a] mb-2">
                    <MapPin size={18} className="text-[#a5dae0]" />
                    {area.region}
                  </h3>
                  <p className="text-sm text-[#666666] leading-relaxed">{area.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-16"
          >
            <p className="text-[#555555] mb-4">Ready to book your mobile bar?</p>
            <div className="flex flex-wrap justify-center gap-4">
              <button onClick={openBookingModal} className="bg-[#a5dae0] text-[#1a1a1a] px-8 py-3 rounded-full font-semibold hover:bg-[#8bc9d0] transition-all inline-flex items-center gap-2 text-sm">
                Get Your Quote <ArrowRight size={16} />
              </button>
              <Link to="/contact" className="px-6 py-3 rounded-full border-2 border-gray-200 text-[#444444] hover:border-[#a5dae0] hover:text-[#a5dae0] transition-all text-sm font-medium">Contact Us</Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
